/**
 * FAB — Floating voice action button.
 * Visible on non-chat tabs. Records audio via getUserMedia,
 * transcribes via API, then sends as a chat message.
 */

import { useState, useRef, useCallback } from 'preact/hooks';
import { useRouter } from 'preact-router';
import { api } from '../core/api/client';
import { sendMessage } from '../modules/chat/chat-store';
import styles from './FAB.module.css';

type FABState = 'idle' | 'recording' | 'processing';

export function FAB() {
  const [routerState] = useRouter();
  const currentUrl = routerState?.url ?? '/';

  const [state, setState] = useState<FABState>('idle');
  const [statusText, setStatusText] = useState('');
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  // Hide on tabs that have their own inline mic buttons
  const isChatTab = currentUrl === '/' || currentUrl.startsWith('/chat');
  const isApprovalsTab = currentUrl.startsWith('/approvals');
  if (isChatTab || isApprovalsTab) return null;

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      chunksRef.current = [];

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus'
          : 'audio/webm',
      });

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = async () => {
        // Stop all tracks to release mic
        stream.getTracks().forEach((t) => t.stop());

        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        if (blob.size === 0) {
          setState('idle');
          setStatusText('');
          return;
        }

        setState('processing');
        setStatusText('Transcribing...');

        try {
          const { text } = await api.transcribe(blob);
          if (text.trim()) {
            setStatusText('Sending...');
            await sendMessage(text.trim());
            setStatusText('Sent');
          } else {
            setStatusText('No speech detected');
          }
        } catch (err) {
          console.error('[FAB] Transcription error:', err);
          setStatusText('Error');
        }

        // Reset after brief delay
        setTimeout(() => {
          setState('idle');
          setStatusText('');
        }, 1500);
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setState('recording');
      setStatusText('Listening...');
    } catch (err) {
      console.error('[FAB] getUserMedia error:', err);
      setStatusText('Mic blocked');
      setTimeout(() => setStatusText(''), 2000);
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
  }, []);

  const handleClick = useCallback(() => {
    if (state === 'idle') {
      startRecording();
    } else if (state === 'recording') {
      stopRecording();
    }
    // 'processing' state ignores clicks (via CSS pointer-events: none)
  }, [state, startRecording, stopRecording]);

  const btnClass = [
    styles.btn,
    state === 'recording' ? styles.recording : '',
    state === 'processing' ? styles.processing : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div class={styles.fab}>
      <button
        class={btnClass}
        onClick={handleClick}
        title={state === 'recording' ? 'Stop recording' : 'Voice command'}
        aria-label={state === 'recording' ? 'Stop recording' : 'Voice command'}
      >
        {state === 'recording' ? (
          /* Stop icon */
          <svg viewBox="0 0 24 24" fill="currentColor">
            <rect x="6" y="6" width="12" height="12" rx="2" />
          </svg>
        ) : (
          /* Mic icon */
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
            <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
            <line x1="12" y1="19" x2="12" y2="23" />
            <line x1="8" y1="23" x2="16" y2="23" />
          </svg>
        )}
      </button>
      {statusText && <div class={styles.status}>{statusText}</div>}
    </div>
  );
}
